// src/components/auth/RegisterModal.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, writeBatch } from 'firebase/firestore';
import { auth, firestore } from '../../config/firebase';
import { User } from '../../types';
import { validateUsername } from '../../services/UserValidationService';
import '../../styles/layout/Modal.css';

interface RegisterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    onSwitchToLogin?: () => void;
}

const STRENGTH_LABELS = ['', 'Nõrk', 'Nõrk', 'Keskmine', 'Tugev', 'Väga tugev'];
const STRENGTH_COLORS = ['', '#ff6b5e', '#ff6b5e', '#ffb020', '#4fd1c5', '#4fd1c5'];

export const RegisterModal: React.FC<RegisterModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    onSwitchToLogin
}) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);

    const firstFieldRef = useRef<HTMLSelectElement | HTMLInputElement>(null);
    // The debounce timer must survive re-renders. Previously the debounce helper was
    // rebuilt on every render, so each keystroke got its own timer and none were ever
    // cancelled — one Firestore read per character typed.
    const debounceRef = useRef<number | undefined>(undefined);
    // Guards against a slow earlier check overwriting a newer one.
    const requestIdRef = useRef(0);

    const [usernameValidation, setUsernameValidation] = useState<{
        isValid: boolean;
        isAvailable: boolean;
        message: string;
        isChecking: boolean;
    }>({
        isValid: false,
        isAvailable: false,
        message: '',
        isChecking: false
    });


    const runUsernameCheck = useCallback(async (usernameToCheck: string) => {
        const requestId = ++requestIdRef.current;
        setUsernameValidation(prev => ({ ...prev, isChecking: true }));

        const result = await validateUsername(usernameToCheck);

        if (requestId !== requestIdRef.current) return;

        setUsernameValidation({
            isValid: result.isValid,
            isAvailable: result.isAvailable,
            message: result.message,
            isChecking: false
        });
    }, []);

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUsername = e.target.value;
        setUsername(newUsername);

        if (error) setError('');

        window.clearTimeout(debounceRef.current);

        if (newUsername.length < 3) {
            requestIdRef.current++;
            setUsernameValidation({
                isValid: false,
                isAvailable: false,
                message: '',
                isChecking: false
            });
            return;
        }

        debounceRef.current = window.setTimeout(() => runUsernameCheck(newUsername), 500);
    };

    useEffect(() => () => window.clearTimeout(debounceRef.current), []);

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, onClose]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            window.clearTimeout(debounceRef.current);
            requestIdRef.current++;
            setEmail('');
            setPassword('');
            setConfirmPassword('');
            setUsername('');
            setError('');
            setShowPassword(false);
            setPasswordStrength(0);
            setUsernameValidation({
                isValid: false,
                isAvailable: false,
                message: '',
                isChecking: false
            });
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            const timer = window.setTimeout(() => firstFieldRef.current?.focus(), 60);
            return () => window.clearTimeout(timer);
        }
    }, [isOpen]);

    // Calculate password strength
    useEffect(() => {
        let strength = 0;
        if (password.length >= 6) strength++;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        setPasswordStrength(strength);
    }, [password]);

    if (!isOpen) return null;

    const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;

    const validateForm = (): boolean => {
        if (!usernameValidation.isValid || !usernameValidation.isAvailable) {
            setError('Kasutajanimi ei ole kehtiv või on juba kasutusel');
            return false;
        }

        if (password.length < 6) {
            setError('Parool peab olema vähemalt 6 tähemärki pikk');
            return false;
        }
        if (password !== confirmPassword) {
            setError('Paroolid ei ühti');
            return false;
        }
        return true;
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const { user } = await createUserWithEmailAndPassword(auth, email, password);

            // Create user profile (same for all servers)
            const userProfile: User = {
                uid: user.uid,
                email: user.email!,
                username,
                usernameLower: username.toLowerCase(),
                createdAt: new Date()
            };

            // Write the account record and claim the public username reservation
            // together, so a half-registered account cannot leave the name unclaimed.
            const batch = writeBatch(firestore);
            batch.set(doc(firestore, 'users', user.uid), userProfile);
            batch.set(doc(firestore, 'usernames', username.toLowerCase()), { uid: user.uid });
            await batch.commit();

            // Send the confirmation email. A failure here must not fail the registration —
            // the account exists and is usable; verification can be resent from settings.
            try {
                await sendEmailVerification(user);
            } catch (verificationError) {
                console.error('Could not send verification email:', verificationError);
            }

            // No character is created here any more. Registration makes an account; the
            // world selection page is where a character is chosen, and the profile for a
            // world is created the first time the player enters it. Creating one at sign-up
            // meant every account was born into whichever world the form happened to name.
            onSuccess();
            onClose();
        } catch (error: any) {
            if (error.code === 'auth/email-already-in-use') {
                setError('See e-posti aadress on juba kasutusel');
            } else if (error.code === 'auth/weak-password') {
                setError('Parool on liiga nõrk');
            } else if (error.code === 'auth/invalid-email') {
                setError('Vigane e-posti aadress');
            } else {
                setError('Registreerimine ebaõnnestus. Proovi uuesti.');
            }
        } finally {
            setLoading(false);
        }
    };

    const usernameStateClass = () => {
        if (username.length < 3 || usernameValidation.isChecking) return '';
        return usernameValidation.isAvailable ? ' vm-input--ok' : ' vm-input--bad';
    };

    return (
        <div
            className="vm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vm-register-title"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="vm-modal__panel">
                <button
                    type="button"
                    className="vm-modal__close"
                    onClick={onClose}
                    aria-label="Sulge"
                >
                    ×
                </button>

                <div className="vm-modal__head">
                    <p className="vm-modal__eyebrow">Uus teenistuja</p>
                    <h2 className="vm-modal__title" id="vm-register-title">Loo konto</h2>
                    <p className="vm-modal__sub">
                        Tasuta ja otse brauseris. Alustad politseikoolist.
                    </p>
                </div>

                <form onSubmit={handleRegister} className="vm-form">
                    <div className="vm-field">
                        <label className="vm-field__label" htmlFor="vm-reg-username">
                            Kasutajanimi
                        </label>
                        <div className="vm-input-wrap">
                            <input
                                id="vm-reg-username"
                                ref={firstFieldRef as React.RefObject<HTMLInputElement>}
                                type="text"
                                placeholder="3–20 tähemärki"
                                value={username}
                                onChange={handleUsernameChange}
                                required
                                className={`vm-input${usernameStateClass()}`}
                                disabled={loading}
                                autoComplete="username"
                                maxLength={20}
                                aria-describedby="vm-reg-username-hint"
                            />
                            {username.length >= 3 && (
                                <span className="vm-input-affix vm-input-affix--static" aria-hidden="true">
                                    {usernameValidation.isChecking
                                        ? '…'
                                        : usernameValidation.isAvailable ? '✓' : '✕'}
                                </span>
                            )}
                        </div>
                        {username.length >= 3 && usernameValidation.message && (
                            <p
                                id="vm-reg-username-hint"
                                className={`vm-hint ${usernameValidation.isAvailable ? 'vm-hint--ok' : 'vm-hint--bad'}`}
                            >
                                {usernameValidation.message}
                            </p>
                        )}
                    </div>

                    <div className="vm-field">
                        <label className="vm-field__label" htmlFor="vm-reg-email">
                            E-posti aadress
                        </label>
                        <input
                            id="vm-reg-email"
                            type="email"
                            placeholder="sinu@email.ee"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="vm-input"
                            disabled={loading}
                            autoComplete="email"
                        />
                    </div>

                    <div className="vm-field">
                        <label className="vm-field__label" htmlFor="vm-reg-password">
                            Parool
                        </label>
                        <div className="vm-input-wrap">
                            <input
                                id="vm-reg-password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Vähemalt 6 tähemärki"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                className="vm-input"
                                disabled={loading}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="vm-input-affix"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                                aria-label={showPassword ? 'Peida parool' : 'Näita parooli'}
                            >
                                {showPassword ? 'Peida' : 'Näita'}
                            </button>
                        </div>

                        {password && (
                            <div className="vm-strength">
                                <div
                                    className="vm-strength__track"
                                    role="img"
                                    aria-label={`Parooli tugevus: ${STRENGTH_LABELS[passwordStrength]}`}
                                >
                                    {[1, 2, 3, 4, 5].map((seg) => (
                                        <span
                                            key={seg}
                                            className="vm-strength__seg"
                                            style={
                                                seg <= passwordStrength
                                                    ? { backgroundColor: STRENGTH_COLORS[passwordStrength] }
                                                    : undefined
                                            }
                                        />
                                    ))}
                                </div>
                                <span
                                    className="vm-strength__label"
                                    style={{ color: STRENGTH_COLORS[passwordStrength] || undefined }}
                                >
                                    {STRENGTH_LABELS[passwordStrength]}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="vm-field">
                        <label className="vm-field__label" htmlFor="vm-reg-confirm">
                            Kinnita parool
                        </label>
                        <input
                            id="vm-reg-confirm"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Sisesta parool uuesti"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength={6}
                            className={`vm-input${
                                confirmPassword.length === 0 ? '' : passwordsMatch ? ' vm-input--ok' : ' vm-input--bad'
                            }`}
                            disabled={loading}
                            autoComplete="new-password"
                        />
                        {confirmPassword.length > 0 && !passwordsMatch && (
                            <p className="vm-hint vm-hint--bad">Paroolid ei ühti</p>
                        )}
                    </div>

                    {error && (
                        <p className="vm-alert" role="alert">
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        className="vm-submit"
                        disabled={
                            loading ||
                            !email ||
                            !password ||
                            !username ||
                            !confirmPassword ||
                            !usernameValidation.isAvailable ||
                            usernameValidation.isChecking
                        }
                    >
                        {loading ? <><span className="vm-spinner" />Registreerin…</> : 'Loo konto'}
                    </button>
                </form>

                {onSwitchToLogin && (
                    <p className="vm-modal__foot">
                        Konto juba olemas?{' '}
                        <button type="button" className="vm-linkbtn" onClick={onSwitchToLogin}>
                            Logi sisse
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
};
